import {
  Autocomplete,
  Button,
  Card,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useRef, useState } from 'react';
import { IoMdTrash } from 'react-icons/io';
import { Uploader } from 'src/components';
import Editor from 'src/components/editor';
import { STEP_CONTENT_TYPE } from 'src/helpers';
import { STEP_FILE_TYPE } from 'src/helpers/CourseContentType';
import { UPLOAD_MEDIA } from 'src/services/paths';

export const StepProperties = ({ properties, setProperties, error, stepHasMediaFile, setStepHasMediaFile }) => {
  const inputRef = useRef(null);
  const [isUploading, setIsUploading] = useState({});
  const [progress, setProgress] = useState({});

  function handleAppendStepMedia() {
    setStepHasMediaFile(true);
    const newMedia = {
      _id: new Date().toISOString(),
      name: '',
      type: { label: '', type: '', accept: '' },
      hash: '',
      fileName: '',
    };
    setProperties((properties) => ({ ...properties, medias: [...properties.medias, newMedia] }));
  }
  function handleDeleteMediaFile(mediaIndex) {
    setProperties((properties) => ({
      ...properties,
      medias: [...properties.medias.slice(0, mediaIndex), ...properties.medias?.slice(mediaIndex + 1)],
    }));
    if (properties.medias.length - 1 === 0) {
      setStepHasMediaFile(false);
    }
  }
  async function handleUploadMediaFile(files, mediaType, mediaIndex) {
    const file = files[0];
    if (file) {
      setIsUploading({ ...isUploading, [mediaIndex]: true });
      try {
        const body = new FormData();
        body.append('media', file);
        body.append('mediaType', mediaType);
        const response = await axios.post(UPLOAD_MEDIA, body, {
          timeout: 0,
          onUploadProgress: (e) => {
            const percent = e.progress * 99;
            setProgress({ ...progress, [mediaIndex]: percent });
            if (e.loaded === e.total) {
              setProgress({ ...progress, [mediaIndex]: 0 });
              setIsUploading({ ...isUploading, [mediaIndex]: false });
            }
          },
        });
        handleChangeStepMedia('fileName', file.name, mediaIndex);
        handleChangeStepMedia('hash', response.data.mediaHash, mediaIndex);
      } catch (error) {
        console.log('UPLOAD ERROR', error);
        enqueueSnackbar('خطا در بارگذاری فایل - لطفا مجددا تلاش کنید', { variant: 'error' });
        setIsUploading({ ...isUploading, [mediaIndex]: false });
      }
    }
  }
  function handleSwitchStepHasMedia() {
    if (stepHasMediaFile) {
      setStepHasMediaFile(false);
      setProperties((properties) => ({ ...properties, medias: [] }));
    } else {
      setStepHasMediaFile(true);
      handleAppendStepMedia();
    }
  }
  function handleChangeStepMedia(key, value, mediaId) {
    const mediaIndex = properties.medias?.findIndex((m) => m._id === mediaId);
    setProperties((properties) => ({
      ...properties,
      medias: [
        ...properties.medias.slice(0, mediaIndex),
        { ...properties.medias[mediaIndex], [key]: value },
        ...properties.medias?.slice(mediaIndex + 1),
      ],
    }));
  }

  const renderContent = useCallback(() => {
    switch (properties.type.value) {
      case 0:
        return (
          <Uploader
            type={0}
            defaultSetting={{
              multi: true,
            }}
            media={properties.slideHashes}
            setMedia={(slideHashes) => {
              if (Array.isArray(slideHashes)) return;
              setProperties({ ...properties, slideHashes });
            }}
            error={error && properties.type.value === 0 && properties.slideHashes.length === 0}
            properties={properties}
            setProperties={setProperties}
          />
        );
      case 1:
        return (
          <Uploader
            type={1}
            media={properties.content}
            setMedia={(a) => setProperties({ ...properties, content: a })}
            error={
              (error && properties.type.value === 1 && properties.content === '') || Array.isArray(properties.content)
            }
            properties={properties}
            setProperties={setProperties}
          />
        );
      case 2:
        return (
          <Uploader
            type={2}
            media={properties.content}
            setMedia={(c) => setProperties({ ...properties, content: c })}
            error={
              (error && properties.type.value === 2 && properties.content === '') || Array.isArray(properties.content)
            }
            properties={properties}
            setProperties={setProperties}
          />
        );
      case 3:
        return (
          <Editor
            simple
            id="description"
            value={properties.html}
            onChange={(t) => setProperties({ ...properties, html: t })}
            placeholder="متن خود را اینجا بنویسید"
            error={
              (error && properties.html === '') || (properties.type.value === 3 && properties.html === '<p><br></p>')
            }
          />
        );

      default:
        break;
    }
  }, [properties]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <TextField
          label="عنوان گام"
          fullWidth
          value={properties.title}
          onChange={(e) => setProperties({ ...properties, title: e.target.value })}
          error={error && properties.title === ''}
          autoComplete="off"
        />
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          options={STEP_CONTENT_TYPE}
          getOptionLabel={(option) => option.label || ''}
          value={properties.type}
          onChange={(e, newValue) => {
            setProperties({ ...properties, type: newValue, content: '', slideHashes: [] });
          }}
          renderInput={(params) => (
            <TextField {...params} label="نوع محتوا" error={error && properties.type.value === undefined} />
          )}
          isOptionEqualToValue={(option) => option.value === properties.type.value}
          disableClearable
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label="زمان مطالعه محتوا (دقیقه)"
          fullWidth
          type="number"
          inputProps={{ min: 0 }}
          value={properties.stepTime} 
          onChange={(e) => {
            const value = e.target.value;
            if (value === '') {
              setProperties({ ...properties, stepTime: '' });
              return;
            }
            const numValue = Number(value);
            if (numValue < 0) return;
            setProperties({ ...properties, stepTime: numValue });
          }}
          onKeyDown={(e) => {
            if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
              e.preventDefault();
            }
          }}
          error={error && properties.stepTime === ''}
          autoComplete="off"
        />
      </Grid>
      <Grid item xs={12}>
        {renderContent()}
      </Grid>
      <Grid item xs={12}>
        <Stack gap={3}>
          <FormControlLabel
            control={<Switch />}
            label={'پیوست'}
            onChange={handleSwitchStepHasMedia}
            checked={stepHasMediaFile}
          />
          {properties?.medias?.map((media, mediaIndex) => {
            return (
              <Card sx={{ p: 3 }} key={media._id}>
                <Stack spacing={3}>
                  <Stack flexDirection={'row'} justifyContent={'space-between'}>
                    <Typography variant="button">{`فایل شماره ${mediaIndex + 1}`}</Typography>

                    <IconButton color="error" onClick={() => handleDeleteMediaFile(mediaIndex)}>
                      <IoMdTrash />
                    </IconButton>
                  </Stack>
                  <Autocomplete
                    options={STEP_FILE_TYPE}
                    getOptionLabel={(option) => option.label || ''}
                    value={media?.type}
                    onChange={(e, newValue) => {
                      handleChangeStepMedia('type', newValue, media._id);
                      handleChangeStepMedia('hash', '', media._id);
                      handleChangeStepMedia('fileName', '', media._id);
                    }}
                    renderInput={(params) => <TextField {...params} label="نوع فایل" />}
                    disableClearable
                  />
                  <TextField
                    label={'نام فایل'}
                    value={media?.name}
                    onChange={(event) => handleChangeStepMedia('name', event.target.value, media._id)}
                  />
                  <input
                    ref={inputRef}
                    type="file"
                    accept={media?.type?.accept}
                    hidden
                    onChange={(event) => handleUploadMediaFile(event.target.files, media?.type?.type, media._id)}
                    onClick={(event) => (event.target.value = null)}
                  />
                  <Stack spacing={1}>
                    <TextField
                      disabled={media?.type?.accept === ''}
                      label={'نام فایل'}
                      value={media?.fileName}
                      InputProps={{
                        endAdornment: (
                          <Button
                            disabled={media?.type?.accept === '' || isUploading[media._id]}
                            onClick={() => inputRef?.current?.click()}
                          >
                            {'بارگذاری'}
                          </Button>
                        ),
                      }}
                    />
                    {isUploading[media._id] && (
                      <LinearProgress defaultValue={0} value={progress[media._id]} variant="determinate" />
                    )}
                  </Stack>
                </Stack>
              </Card>
            );
          })}
          {stepHasMediaFile && (
            <Grid item xs={6}>
              <Button variant="contained" onClick={handleAppendStepMedia}>
                {'افزودن'}
              </Button>
            </Grid>
          )}
        </Stack>
      </Grid>
    </Grid>
  );
};
